import { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiAlertCircle, FiCheckCircle, FiLoader, FiSend } from 'react-icons/fi';
import { submitSupportRequest } from '../lib/api';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMPTY_VALUES = { name: '', email: '', subject: '', message: '' };

const inputClass =
  'w-full rounded-xl border border-line bg-white px-4 py-2.5 text-sm text-ink outline-none transition focus:border-brand-400 focus:ring-2 focus:ring-brand-100 dark:border-midnight-600 dark:bg-midnight-800 dark:text-white dark:focus:ring-brand-900/40';

function validate(values) {
  const errors = {};
  if (!values.name.trim()) errors.name = 'Name is required';
  if (!EMAIL_RE.test(values.email.trim())) errors.email = 'Enter a valid email address';
  if (!values.subject.trim()) errors.subject = 'Subject is required';
  if (!values.message.trim()) errors.message = 'Message is required';
  return errors;
}

export default function SupportPage() {
  const [values, setValues] = useState(EMPTY_VALUES);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | sending | error
  const [errorMessage, setErrorMessage] = useState('');
  const [ticketNumber, setTicketNumber] = useState(null);

  const handleChange = (field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formErrors = validate(values);
    setErrors(formErrors);
    if (Object.keys(formErrors).length > 0) return;

    setStatus('sending');
    setErrorMessage('');

    try {
      const response = await submitSupportRequest({
        name: values.name.trim(),
        email: values.email.trim(),
        subject: values.subject.trim(),
        message: values.message.trim(),
      });
      setTicketNumber(response.ticketNumber);
    } catch (err) {
      setErrorMessage(err.message || 'Could not send your message. Please try again.');
      setStatus('error');
    }
  };

  if (ticketNumber) {
    return (
      <section className="container-page py-24">
        <div className="card mx-auto flex max-w-lg flex-col items-center gap-4 p-10 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-success/10 text-success">
            <FiCheckCircle size={28} />
          </span>
          <h2 className="text-2xl font-bold text-ink dark:text-white">Your message has been received</h2>
          <p className="text-sm leading-relaxed text-muted dark:text-subtle">
            We've sent a confirmation to <strong>{values.email.trim()}</strong>. Our team will get in touch with you
            in the shortest time possible.
          </p>
          <div className="flex flex-col items-center gap-1 rounded-xl border border-brand-200 bg-brand-gradient-soft px-6 py-4 dark:border-brand-800/60">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-subtle">Your ticket number</span>
            <span className="font-mono text-lg font-bold text-ink dark:text-white">{ticketNumber}</span>
          </div>
          <p className="text-xs text-subtle">Please keep this reference for any follow-up.</p>
          <Link to="/" className="btn-primary mt-2">
            Back to home
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 sm:py-20">
      <div className="container-page mx-auto max-w-xl">
        <div className="mb-8 text-center">
          <span className="section-eyebrow">Support</span>
          <h1 className="mt-4 text-3xl font-extrabold tracking-tight text-ink dark:text-white sm:text-4xl">
            Get in touch
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-muted dark:text-subtle">
            Have a question or found something that could be better? Send us a message and we'll get back to you as
            soon as possible.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="card flex flex-col gap-5 p-8">
          {status === 'error' && (
            <div className="flex items-start gap-2 rounded-xl border border-danger/30 bg-danger/5 p-4 text-sm text-danger">
              <FiAlertCircle className="mt-0.5 flex-none" size={16} />
              <span>{errorMessage}</span>
            </div>
          )}

          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-ink dark:text-white">Name</span>
            <input
              type="text"
              className={inputClass}
              value={values.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Jane Muthoni"
              maxLength={100}
            />
            {errors.name && <span className="text-xs text-danger">{errors.name}</span>}
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-ink dark:text-white">Email</span>
            <input
              type="email"
              className={inputClass}
              value={values.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="jane@example.com"
              maxLength={100}
            />
            {errors.email && <span className="text-xs text-danger">{errors.email}</span>}
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-ink dark:text-white">Subject</span>
            <input
              type="text"
              className={inputClass}
              value={values.subject}
              onChange={(e) => handleChange('subject', e.target.value)}
              placeholder="What's this about?"
              maxLength={150}
            />
            {errors.subject && <span className="text-xs text-danger">{errors.subject}</span>}
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-ink dark:text-white">Message</span>
            <textarea
              className={`${inputClass} min-h-[140px] resize-y`}
              value={values.message}
              onChange={(e) => handleChange('message', e.target.value)}
              placeholder="Tell us what's going on…"
              maxLength={5000}
            />
            {errors.message && <span className="text-xs text-danger">{errors.message}</span>}
          </label>

          <button type="submit" disabled={status === 'sending'} className="btn-primary w-full disabled:cursor-not-allowed disabled:opacity-70">
            {status === 'sending' ? (
              <>
                <FiLoader className="animate-spin" /> Sending…
              </>
            ) : (
              <>
                <FiSend /> Send message
              </>
            )}
          </button>
        </form>
      </div>
    </section>
  );
}
